import { useCallback, useState } from "react";
import {
  Box,
  ContextMenuCheckboxItem,
  ContextMenuItem,
  ContextMenuSeparator,
  Icon,
  TabsTab,
  TabsTabInput,
  TabsViewIcon,
  Text,
} from "@salestrak/ui";

import { useDashboardViewFavorite } from "#app/components/dashboards/use-dashboard-favorites";
import type { EntityViewRow } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useCopyWidgetBoard } from "#app/lib/hooks/use-copy-widget-board";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useViews } from "#app/lib/views/view-context";
import { viewPath } from "#app/lib/views/view-routes";
import { NO_PERMISSION } from "#app/components/menus/disabled-reason";
import { DeleteViewDialog } from "./delete-view-dialog";
import { ViewEvent } from "#app/lib/analytics";
import { useCopyViewLink } from "./use-copy-view-link";
import { viewTypeSeed, viewTabVisual, viewVisual } from "./view-type-meta";

/**
 * One tab of a scope's view rail: the draggable tab itself, its right-click
 * menu, the rename that happens in the tab's own label, and the delete
 * confirmation.
 *
 * The menu acts on THIS view, which is not necessarily the mounted one — a
 * right-click reaches any tab in the row. So every action here names the view
 * explicitly, and the one that only makes sense for what is on screen
 * (Customize view — the Customize view panel) navigates to it first.
 *
 * `create.views` disables the writes rather than hiding them: the rail is the
 * same rail for every viewer, and a menu whose rows come and go by role makes
 * the same tab a different tab depending on who opened it.
 *
 * A DASHBOARD tab carries one more row, Favorite: the viewer's own star, the
 * same one the Dashboards directory and its module menu flip, so a dashboard
 * can join the module's Favorites shelf from the strip it lives on. It is
 * personal (never behind `create.views`) and it is the only row that varies
 * by view TYPE — other view types have no favorites shelf to join.
 *
 * One state rides the tab's `trailing` slot as a 14px glyph in the label's
 * own ink: the pin, for a PINNED view. The scope's DEFAULT view wears no
 * mark: where the scope opens is stated only where it is set — the switch in
 * this menu and on the Customize view main page — since a reader on the
 * strip has already landed. Both are the row's flags (`isDefault`, at most
 * one per scope; `isPinned`), set behind `create.views` — a pin is the
 * workspace's, so every member's strip leads with the same run. The pin has
 * exactly this one slot on the tab — the identity tile is never badged,
 * because at 14px a badge is an 8px smudge that needs its own offset per tile
 * shape and custom glyph.
 */

export type ViewTabProps = {
  view: EntityViewRow;
  /** The tab lost its slot to the rail's overflow — forwarded to
   *  `TabsTab`, which keeps it mounted as an unpainted measurable box while
   *  the `ViewOverflowMenu` beside the strip offers the view instead. */
  overflowHidden?: boolean;
};

export function ViewTab({ view, overflowHidden }: ViewTabProps) {
  const {
    scope,
    views,
    activeView,
    canManage,
    setPinned,
    createView,
    updateView,
    trackView,
    setDefaultView,
    openPanel,
    requestPanelForView,
  } = useViews();
  const { replace } = useAppNavigate();
  const copyWidgetBoard = useCopyWidgetBoard();

  const [renaming, setRenaming] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const visual = viewVisual(view);
  const tabVisual = viewTabVisual(view);
  const isActive = activeView?.id === view.id;
  // The delete floor is one view of each REQUIRED type (List, Board), not
  // the seeded rows: a built-in can go once a same-type sibling exists, and a
  // dashboard can always go. The server enforces the same floor, so a stale
  // count still fails safely.
  const isLastOfType =
    viewTypeSeed(view).required &&
    views.filter((row) => row.type === view.type).length <= 1;
  const deleteDisabledReason = !canManage
    ? NO_PERMISSION
    : view.isDefault
      ? "Can't delete the default view"
      : isLastOfType
        ? `Must have at least one ${viewTypeSeed(view).label} view`
        : null;

  // The Customize view panel belongs to the MOUNTED view, so a right-click on
  // another tab has to land there first. The pending open is armed on the
  // ViewsProvider — the navigation remounts this tab (the workflow shell
  // re-keys its filter scope per view), so state held here would die before
  // the view becomes active.
  const handleDisplayOptions = useCallback(() => {
    if (isActive) {
      openPanel("main", "tab_menu");
      return;
    }
    requestPanelForView(view.id);
    replace(viewPath(scope, view));
  }, [isActive, openPanel, replace, requestPanelForView, scope, view]);

  const handleCopyLink = useCopyViewLink(view, "tab_menu");
  const favorite = useDashboardViewFavorite(view, scope, "tab_menu");

  // The switch's value is the truth: a stale flag can never re-pin what the
  // press meant to release.
  const handleTogglePin = useCallback(
    (checked: boolean) => {
      void setPinned(view.id, checked);
    },
    [setPinned, view.id],
  );
  const handleSetDefault = useCallback(
    (checked: boolean) => {
      void setDefaultView(view.id, checked);
    },
    [setDefaultView, view.id],
  );

  const handleDuplicate = useCallback(async () => {
    const created = await createView(
      {
        type: view.type,
        name: `${view.name} copy`,
        icon: { icon: visual.icon, color: visual.color },
        config: view.config,
      },
      "tab_duplicate",
    );
    if (!created) return;
    // A dashboard's widgets are rows keyed by the view's board key, not part
    // of `config` — copy them into the new board before landing on it. A new
    // entity-view board is never server-seeded, so the copy is the whole
    // board.
    if (view.widgetView && created.widgetView) {
      try {
        await copyWidgetBoard(view.widgetView, created.widgetView);
      } catch (err) {
        // The view row exists — land on it with whatever copied.
        toastMutationError(err, "Failed to copy widgets");
      }
    }
    replace(viewPath(scope, created));
  }, [copyWidgetBoard, createView, replace, scope, view, visual.color, visual.icon]);

  const commitRename = useCallback(
    (next: string) => {
      setRenaming(false);
      void updateView(view.id, { name: next }).then((saved) => {
        if (saved) trackView(ViewEvent.Rename, saved, { via: "tab_menu" });
      });
    },
    [trackView, updateView, view.id],
  );

  return (
    <>
      <TabsTab
        value={view.id}
        editing={renaming}
        overflowHidden={overflowHidden}
        // No `color`: the mark takes the tab's own ink, muted at rest and
        // full on the active tab, like the label it follows.
        trailing={
          view.isPinned ? (
            <Icon aria-label="Pinned" name="pin" size="xs" />
          ) : undefined
        }
        contextMenu={
          <>
            <ContextMenuItem
              disabled={!canManage}
              disabledReason={canManage ? undefined : NO_PERMISSION}
              onClick={() => setRenaming(true)}
            >
              <Icon color="muted" name="pencil" size="md" />
              <Text>Rename...</Text>
            </ContextMenuItem>
            {favorite ? (
              <ContextMenuItem onClick={favorite.toggle}>
                <Icon
                  color="muted"
                  name={favorite.favorited ? "star-filled" : "star"}
                  size="md"
                />
                <Text>{favorite.favorited ? "Unfavorite" : "Favorite"}</Text>
              </ContextMenuItem>
            ) : null}
            <ContextMenuItem onClick={handleCopyLink}>
              <Icon color="muted" name="link" size="md" />
              <Text>Copy link to view</Text>
            </ContextMenuItem>
            <ContextMenuItem onClick={handleDisplayOptions}>
              <Icon color="muted" name="settings" size="md" />
              <Text>Customize view</Text>
            </ContextMenuItem>
            <ContextMenuSeparator />
            {/* The two STATE rows are switches, so the menu shows each one
                as it stands and flips it in place — a checkbox item keeps
                the menu open, so the flip is seen where it was made. Both
                write the shared row, so both sit behind `create.views`. */}
            <ContextMenuCheckboxItem
              checked={view.isPinned}
              disabled={!canManage}
              disabledReason={canManage ? undefined : NO_PERMISSION}
              onCheckedChange={handleTogglePin}
              variant="switch"
            >
              <Box align="center" direction="row" gap="sm">
                <Icon color="muted" name="pin" size="sm" />
                <Text>Pin view</Text>
              </Box>
            </ContextMenuCheckboxItem>
            {/* At most one default per scope: ON moves the role here (the
                previous default's switch goes off with it); OFF leaves the
                scope with none, and a first visit opens on the first view in
                the row. The default is the FIRST-visit landing only — the bare
                workflow URL reopens the viewer's last view (`useLastView`).
                The panel row carries that sentence; a menu row has no second
                line. */}
            <ContextMenuCheckboxItem
              checked={view.isDefault}
              disabled={!canManage}
              disabledReason={canManage ? undefined : NO_PERMISSION}
              onCheckedChange={handleSetDefault}
              variant="switch"
            >
              <Box align="center" direction="row" gap="sm">
                <Icon color="muted" name="home" size="sm" />
                <Text>Set as default view</Text>
              </Box>
            </ContextMenuCheckboxItem>
            <ContextMenuSeparator />
            <ContextMenuItem
              disabled={!canManage}
              disabledReason={canManage ? undefined : NO_PERMISSION}
              onClick={() => {
                void handleDuplicate();
              }}
            >
              <Icon color="muted" name="copy" size="md" />
              <Text>Duplicate view</Text>
            </ContextMenuItem>
            {/* The default view is where the scope opens, so it stays until
                the role moves off it — the server refuses the delete too.
                Disabled, the row says why (the menu grammar). */}
            {deleteDisabledReason ? (
              <ContextMenuItem disabled disabledReason={deleteDisabledReason}>
                <Icon color="muted" name="trash" size="md" />
                <Text>Delete view</Text>
              </ContextMenuItem>
            ) : (
              <ContextMenuItem onClick={() => setConfirmDelete(true)}>
                <Icon color="muted" name="trash" size="md" />
                <Text>Delete view</Text>
              </ContextMenuItem>
            )}
          </>
        }
      >
        <TabsViewIcon
          color={tabVisual.color}
          icon={tabVisual.icon}
          variant={tabVisual.custom ? "glyph" : "compact-tile"}
        />
        {renaming ? (
          <TabsTabInput
            aria-label="View name"
            defaultValue={view.name}
            onCancel={() => setRenaming(false)}
            onCommit={commitRename}
          />
        ) : (
          view.name
        )}
      </TabsTab>

      <DeleteViewDialog
        view={view}
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
      />
    </>
  );
}
